'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Settings, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

type AdminProduct = {
  id: string;
  slug: string;
  active: boolean;
  catalogReviewed: boolean;
  basePrice: number;
  providerProductId: string;
  provider?: { name?: string; type?: string };
  specifications?: Record<string, any>;
};

type LocalCatalog = {
  supplierKey: string;
  optionDefinitions: Array<{
    id: string;
    optionId: string;
    label?: string | null;
    active: boolean;
    hidden: boolean;
    values: Array<{
      id: string;
      value: string;
      label?: string | null;
      active: boolean;
      hidden: boolean;
      nullable: boolean;
    }>;
  }>;
  priceRules: Array<{
    id: string;
    ruleType: string;
    optionId?: string | null;
    value?: string | null;
    quantityMin?: number | null;
    quantityMax?: number | null;
    sellCents?: number | null;
    costCents?: number | null;
    active: boolean;
  }>;
};

function euroFromCents(cents?: number | null) {
  if (cents === null || cents === undefined) return '';
  return (Number(cents) / 100).toFixed(2).replace('.', ',');
}

function centsFromEuro(value: string) {
  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? Math.round(numeric * 100) : null;
}

export function AdminProductOverlay({ productId }: { productId: string }) {
  const [token, setToken] = useState<string | null>(null);
  const [product, setProduct] = useState<AdminProduct | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState<LocalCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [expandedEditor, setExpandedEditor] = useState<'quick' | 'tiers' | 'options'>('quick');
  const [tierSearch, setTierSearch] = useState('');
  const [optionSearch, setOptionSearch] = useState('');
  const [bulkMultiplier, setBulkMultiplier] = useState('2.3');
  const [showInactive, setShowInactive] = useState(false);
  const [newTierQuantity, setNewTierQuantity] = useState('');
  const [newTierSellPrice, setNewTierSellPrice] = useState('');
  const [priceMultiplier, setPriceMultiplier] = useState('2.3');
  const [pricingMode, setPricingMode] = useState('api');

  useEffect(() => {
    const adminToken = window.localStorage.getItem('admin_token');
    if (!adminToken) return;
    setToken(adminToken);
    fetch(`/api/admin/products/${productId}`, {
      headers: { Authorization: `Bearer ${adminToken}` },
      cache: 'no-store',
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!data?.product) return;
        setProduct(data.product);
        setPriceMultiplier(String(data.product.specifications?.priceMultiplier || 2.3));
        setPricingMode(String(data.product.specifications?.pricingMode || 'api'));
      })
      .catch(() => undefined);
  }, [productId]);

  useEffect(() => {
    if (!token || !open) return;
    setCatalogLoading(true);
    fetch(`/api/admin/products/${productId}/local-pricing-catalog?supplierKey=printcom`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (data?.success) setCatalog(data as LocalCatalog);
      })
      .catch(() => undefined)
      .finally(() => setCatalogLoading(false));
  }, [token, open, productId]);

  if (!token || !product) return null;
  const providerName = product.provider?.name || 'Leverancier';
  const providerType = String(product.provider?.type || '').toUpperCase();
  const apiPricingLabel =
    providerType === 'PRINTCOM'
      ? 'Print.com API'
      : providerType === 'PRODIGI'
        ? 'Prodigi API'
        : providerType === 'PRINTFUL'
          ? 'Printful API'
          : providerType === 'GELATO'
            ? 'Gelato API'
            : `${providerName} API`;

  const savePatch = async (patch: Partial<AdminProduct> & { specifications?: Record<string, any> }) => {
    setSaving(true);
    try {
      const response = await fetch(`/api/admin/products/${product.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
      if (!response.ok) throw new Error('Opslaan mislukt');
      const data = await response.json();
      setProduct(data.product);
      toast.success('Productbeheer opgeslagen');
    } catch (error: any) {
      toast.error(error.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const savePricing = () => {
    const multiplier = Number(priceMultiplier);
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      toast.error('Marge/multiplier moet groter zijn dan 0');
      return;
    }
    savePatch({
      specifications: {
        ...(product.specifications || {}),
        priceMultiplier: multiplier,
        pricingMode,
        allowPriceFallback: false,
      },
    });
  };

  const patchCatalog = async (body: Record<string, any>) => {
    if (!catalog) return;
    setSaving(true);
    try {
      const response = await fetch(`/api/admin/products/${product.id}/local-pricing-catalog`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supplierKey: catalog.supplierKey || 'printcom',
          reason: 'inline-product-overlay-edit',
          ...body,
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.success === false) throw new Error(data?.error || 'Catalogus opslaan mislukt');

      const refreshed = await fetch(`/api/admin/products/${product.id}/local-pricing-catalog?supplierKey=${catalog.supplierKey || 'printcom'}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      }).then((res) => res.ok ? res.json() : null);
      if (refreshed?.success) setCatalog(refreshed as LocalCatalog);
      toast.success('Catalogus direct bijgewerkt');
    } catch (error: any) {
      toast.error(error.message || 'Catalogus opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const createPriceRule = async () => {
    if (!catalog) return;
    const quantity = Number(newTierQuantity);
    const sellCents = centsFromEuro(newTierSellPrice);
    if (!Number.isFinite(quantity) || quantity <= 0 || sellCents === null || sellCents <= 0) {
      toast.error('Vul een geldig aantal en verkoopprijs in');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`/api/admin/products/${product.id}/local-pricing-catalog`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          supplierKey: catalog.supplierKey || 'printcom',
          type: 'price-rule',
          reason: 'inline-product-overlay-create-tier',
          data: {
            ruleType: 'tier',
            optionId: 'copies',
            value: String(quantity),
            quantityMin: quantity,
            quantityMax: quantity,
            match: { copies: quantity },
            sellCents,
            currency: 'EUR',
            active: true,
            priority: 100,
          },
        }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || data?.success === false) throw new Error(data?.error || 'Staffel aanmaken mislukt');
      setNewTierQuantity('');
      setNewTierSellPrice('');
      const refreshed = await fetch(`/api/admin/products/${product.id}/local-pricing-catalog?supplierKey=${catalog.supplierKey || 'printcom'}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      }).then((res) => res.ok ? res.json() : null);
      if (refreshed?.success) setCatalog(refreshed as LocalCatalog);
      toast.success('Nieuwe staffelregel aangemaakt');
    } catch (error: any) {
      toast.error(error.message || 'Staffel aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  };

  const saveBulkTierMultiplier = () => {
    const multiplier = Number(String(bulkMultiplier).replace(',', '.'));
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      toast.error('Vul een geldige staffel multiplier in');
      return;
    }
    const updates = visiblePriceRules
      .filter((rule) => rule.costCents !== null && rule.costCents !== undefined && Number(rule.costCents) > 0)
      .map((rule) => ({
        type: 'price-rule',
        id: rule.id,
        data: {
          sellCents: Math.round(Number(rule.costCents) * multiplier),
          sellMultiplier: multiplier,
        },
      }));
    if (updates.length === 0) {
      toast.error('Geen staffels met kostprijs gevonden voor bulk multiplier');
      return;
    }
    patchCatalog({ updates, reason: 'inline-bulk-tier-multiplier' });
  };

  const visiblePriceRules = (catalog?.priceRules || [])
    .filter((rule) => rule.ruleType === 'tier' || rule.ruleType === 'base')
    .filter((rule) => showInactive || rule.active)
    .filter((rule) => {
      const needle = tierSearch.trim().toLowerCase();
      if (!needle) return true;
      return [
        rule.ruleType,
        rule.optionId,
        rule.value,
        rule.quantityMin,
        rule.quantityMax,
        euroFromCents(rule.sellCents),
        euroFromCents(rule.costCents),
      ].some((value) => String(value ?? '').toLowerCase().includes(needle));
    })
    .sort((a, b) => Number(a.quantityMin || 0) - Number(b.quantityMin || 0))
    .slice(0, 80);
  const editableOptions = (catalog?.optionDefinitions || [])
    .filter((option) => option.values?.length)
    .filter((option) => showInactive || (option.active && !option.hidden))
    .filter((option) => {
      const needle = optionSearch.trim().toLowerCase();
      if (!needle) return true;
      return [
        option.optionId,
        option.label,
        ...option.values.flatMap((value) => [value.value, value.label]),
      ].some((value) => String(value ?? '').toLowerCase().includes(needle));
    })
    .slice(0, 30);

  return (
    <div className="fixed bottom-4 right-4 z-[1200] max-w-[calc(100vw-2rem)]">
      {!open ? (
        <Button type="button" onClick={() => setOpen(true)} className="shadow-xl">
          <Settings className="mr-2 h-4 w-4" />
          Productbeheer
        </Button>
      ) : (
        <div className="max-h-[calc(100vh-2rem)] w-[520px] max-w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-bold text-slate-950">Productbeheer</p>
              <p className="text-xs text-slate-500">{providerName} · {product.providerProductId}</p>
            </div>
            <button type="button" className="text-sm text-slate-500 hover:text-slate-900" onClick={() => setOpen(false)}>
              Sluiten
            </button>
          </div>

          <div className="mb-3 flex flex-wrap gap-2">
            <Badge className={product.active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
              {product.active ? 'Actief' : 'Uit'}
            </Badge>
            <Badge className={product.catalogReviewed ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}>
              {product.catalogReviewed ? '100/100' : 'Niet gecontroleerd'}
            </Badge>
            <Badge className="bg-slate-100 text-slate-700">{pricingMode}</Badge>
          </div>

          <div className="space-y-3">
            <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3 text-sm">
              <span>
                <span className="block font-medium text-slate-900">Zichtbaar op site</span>
                <span className="block text-xs text-slate-500">Uit = verborgen uit catalogus/productrouting</span>
              </span>
              <Switch
                checked={product.active}
                disabled={saving}
                onCheckedChange={(active) => savePatch({ active })}
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Multiplier</label>
                <Input value={priceMultiplier} onChange={(event) => setPriceMultiplier(event.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Pricing</label>
                <select
                  value={pricingMode}
                  onChange={(event) => setPricingMode(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="api">{apiPricingLabel}</option>
                  <option value="local">Lokale regels</option>
                  <option value="quote">Alleen offerte</option>
                </select>
              </div>
            </div>

            <Button type="button" className="w-full" disabled={saving} onClick={savePricing}>
              <Save className="mr-2 h-4 w-4" />
              Marge/pricing opslaan
            </Button>

            <div className="rounded-md border border-slate-200">
              <div className="grid grid-cols-3 border-b border-slate-200 text-xs font-semibold">
                {[
                  ['quick', 'Snel'],
                  ['tiers', 'Staffels'],
                  ['options', 'Opties'],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`px-3 py-2 ${expandedEditor === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                    onClick={() => setExpandedEditor(id as typeof expandedEditor)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-3 p-3">
                {catalogLoading ? (
                  <p className="text-xs text-slate-500">Catalogus laden...</p>
                ) : !catalog ? (
                  <p className="text-xs text-slate-500">Geen lokale catalogusdata geladen.</p>
                ) : expandedEditor === 'quick' ? (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="rounded-md bg-slate-50 p-2">
                        <span className="block text-slate-500">Opties</span>
                        <span className="text-base font-bold text-slate-900">{catalog.optionDefinitions.length}</span>
                      </div>
                      <div className="rounded-md bg-slate-50 p-2">
                        <span className="block text-slate-500">Prijsregels</span>
                        <span className="text-base font-bold text-slate-900">{catalog.priceRules.length}</span>
                      </div>
                      <div className="rounded-md bg-slate-50 p-2">
                        <span className="block text-slate-500">Uit</span>
                        <span className="text-base font-bold text-slate-900">
                          {catalog.optionDefinitions.filter((option) => option.hidden || !option.active).length}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600">
                      Gebruik Staffels voor directe totaalprijzen en Opties om waardes zichtbaar, verborgen of anders gelabeld te maken.
                    </p>
                  </>
                ) : expandedEditor === 'tiers' ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_90px_90px] gap-2">
                      <Input
                        placeholder="Zoek aantal, prijs, type"
                        value={tierSearch}
                        onChange={(event) => setTierSearch(event.target.value)}
                      />
                      <Input
                        placeholder="x marge"
                        value={bulkMultiplier}
                        onChange={(event) => setBulkMultiplier(event.target.value)}
                      />
                      <Button type="button" variant="outline" disabled={saving} onClick={saveBulkTierMultiplier}>
                        Bulk
                      </Button>
                    </div>
                    <label className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600">
                      <span>Inactieve regels tonen</span>
                      <Switch checked={showInactive} onCheckedChange={setShowInactive} />
                    </label>
                    <div className="grid grid-cols-[70px_1fr_90px_80px] items-end gap-2 rounded-md border border-dashed border-slate-300 p-2">
                      <div>
                        <label className="mb-1 block text-[11px] text-slate-500">Aantal</label>
                        <Input value={newTierQuantity} onChange={(event) => setNewTierQuantity(event.target.value)} />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] text-slate-500">Nieuwe totaalprijs</label>
                        <Input value={newTierSellPrice} onChange={(event) => setNewTierSellPrice(event.target.value)} />
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {newTierQuantity && newTierSellPrice ? `~ ${euroFromCents(Math.round((centsFromEuro(newTierSellPrice) || 0) / Math.max(1, Number(newTierQuantity))))} p/st` : ' '}
                      </div>
                      <Button type="button" variant="outline" disabled={saving} onClick={createPriceRule}>
                        Nieuw
                      </Button>
                    </div>
                    {visiblePriceRules.length === 0 ? (
                      <p className="text-xs text-slate-500">Geen staffelregels gevonden.</p>
                    ) : visiblePriceRules.map((rule) => (
                      <div key={rule.id} className="grid grid-cols-[42px_64px_1fr_1fr_72px_78px] items-end gap-2 rounded-md bg-slate-50 p-2">
                        <div>
                          <label className="mb-1 block text-[11px] text-slate-500">Aan</label>
                          <Switch
                            checked={rule.active}
                            disabled={saving}
                            onCheckedChange={(active) => patchCatalog({
                              type: 'price-rule',
                              id: rule.id,
                              data: { active },
                            })}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] text-slate-500">Aantal</label>
                          <Input
                            value={String(rule.quantityMin || rule.value || '')}
                            disabled={saving}
                            onChange={(event) => {
                              const quantity = Number(event.target.value);
                              setCatalog((prev) => prev ? {
                                ...prev,
                                priceRules: prev.priceRules.map((item) => item.id === rule.id
                                  ? { ...item, quantityMin: quantity, quantityMax: quantity, value: String(quantity) }
                                  : item),
                              } : prev);
                            }}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] text-slate-500">Kostprijs</label>
                          <Input
                            value={euroFromCents(rule.costCents)}
                            disabled={saving}
                            onChange={(event) => {
                              const costCents = centsFromEuro(event.target.value);
                              setCatalog((prev) => prev ? {
                                ...prev,
                                priceRules: prev.priceRules.map((item) => item.id === rule.id ? { ...item, costCents } : item),
                              } : prev);
                            }}
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[11px] text-slate-500">Verkoop</label>
                          <Input
                            value={euroFromCents(rule.sellCents)}
                            disabled={saving}
                            onChange={(event) => {
                              const sellCents = centsFromEuro(event.target.value);
                              setCatalog((prev) => prev ? {
                                ...prev,
                                priceRules: prev.priceRules.map((item) => item.id === rule.id ? { ...item, sellCents } : item),
                              } : prev);
                            }}
                          />
                        </div>
                        <div className="pb-2 text-right text-[11px] font-medium text-slate-600">
                          {rule.sellCents && rule.quantityMin
                            ? `${euroFromCents(Math.round(Number(rule.sellCents) / Math.max(1, Number(rule.quantityMin))))} p/st`
                            : ''}
                          <br />
                          {rule.costCents && rule.sellCents
                            ? `${Math.round(((Number(rule.sellCents) - Number(rule.costCents)) / Number(rule.sellCents)) * 100)}%`
                            : ''}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={saving}
                          onClick={() => patchCatalog({
                            type: 'price-rule',
                            id: rule.id,
                            data: {
                              quantityMin: rule.quantityMin,
                              quantityMax: rule.quantityMax,
                              value: rule.value,
                              sellCents: rule.sellCents,
                              costCents: rule.costCents,
                              active: rule.active,
                            },
                          })}
                        >
                          Opslaan
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-[1fr_120px] gap-2">
                      <Input
                        placeholder="Zoek optie, waarde of label"
                        value={optionSearch}
                        onChange={(event) => setOptionSearch(event.target.value)}
                      />
                      <label className="flex items-center justify-between rounded-md bg-slate-50 px-2 text-xs text-slate-600">
                        <span>Inactief</span>
                        <Switch checked={showInactive} onCheckedChange={setShowInactive} />
                      </label>
                    </div>
                    {editableOptions.length === 0 ? (
                      <p className="text-xs text-slate-500">Geen opties gevonden.</p>
                    ) : editableOptions.map((option) => (
                      <div key={option.id} className="rounded-md bg-slate-50 p-2">
                        <div className="mb-2 grid grid-cols-[1fr_90px_56px_72px] items-center gap-2">
                          <Input
                            value={option.label || option.optionId}
                            disabled={saving}
                            onChange={(event) => setCatalog((prev) => prev ? {
                              ...prev,
                              optionDefinitions: prev.optionDefinitions.map((item) => item.id === option.id
                                ? { ...item, label: event.target.value }
                              : item),
                            } : prev)}
                          />
                          <span className="truncate text-xs text-slate-500">{option.optionId}</span>
                          <Switch
                            checked={option.active && !option.hidden}
                            disabled={saving}
                            onCheckedChange={(checked) => patchCatalog({
                              type: 'option-definition',
                              id: option.id,
                              data: { active: checked, hidden: !checked, label: option.label },
                            })}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={saving}
                            onClick={() => patchCatalog({
                              type: 'option-definition',
                              id: option.id,
                              data: { label: option.label, active: option.active, hidden: option.hidden },
                            })}
                          >
                            Optie
                          </Button>
                        </div>
                        <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                          {option.values
                            .filter((value) => showInactive || (value.active && !value.hidden))
                            .filter((value) => {
                              const needle = optionSearch.trim().toLowerCase();
                              if (!needle) return true;
                              return [value.value, value.label].some((item) => String(item ?? '').toLowerCase().includes(needle));
                            })
                            .slice(0, 60)
                            .map((value) => (
                            <div key={value.id} className="grid grid-cols-[1fr_92px_56px_72px] items-center gap-2">
                              <Input
                                value={value.label || value.value}
                                disabled={saving}
                                onChange={(event) => setCatalog((prev) => prev ? {
                                  ...prev,
                                  optionDefinitions: prev.optionDefinitions.map((item) => item.id === option.id
                                    ? {
                                        ...item,
                                        values: item.values.map((candidate) => candidate.id === value.id
                                          ? { ...candidate, label: event.target.value }
                                          : candidate),
                                      }
                                  : item),
                                } : prev)}
                              />
                              <span className="truncate text-[11px] text-slate-500" title={value.value}>{value.value}</span>
                              <Switch
                                checked={value.active && !value.hidden}
                                disabled={saving}
                                onCheckedChange={(checked) => patchCatalog({
                                  type: 'option-value',
                                  id: value.id,
                                  data: { active: checked, hidden: !checked, label: value.label },
                                })}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                disabled={saving}
                                onClick={() => patchCatalog({
                                  type: 'option-value',
                                  id: value.id,
                                  data: { label: value.label, active: value.active, hidden: value.hidden },
                                })}
                              >
                                Label
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" asChild>
                <Link href={`/admin/products/${product.id}/edit?tab=pricing`}>
                  Opties/prijzen
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href={`/admin/products/${product.id}/edit?tab=specifications`}>
                  Staffels/cache
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" asChild>
                <Link href={`/admin/offertes`}>
                  Offertes
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link href={`/admin/products/${product.id}/edit?tab=general`}>
                  Productkaart
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
